/* eslint-disable import/no-duplicates */
/* eslint-disable react/jsx-curly-newline */
import { useCallback, useState, useEffect, ChangeEvent } from 'react';
import { differenceInSeconds, format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import {
  Box,
  Checkbox,
  Flex,
  Heading,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
  Button,
  Tooltip,
  Icon,
  Text,
  Badge,
  Spinner,
  Center,
  ScaleFade,
  Select as ChakraSelect,
} from '@chakra-ui/react';

import { RiAttachmentLine, RiDraftLine } from 'react-icons/ri';
import { ModalAddNewDriverKilometer } from '../../Modal/ModalAddNewDriverKilometer';
import { ModalEditDriverKilometer } from '../../Modal/ModalEditDriverKilometer';
import { ModalVisualizeImage } from '../../Modal/ModalVisualizeImage/index';
import { apllyToast } from '../../../Global/Toast2.0';
import { Pagination } from '../../Pagination';

import { useAuth } from '../../../../hooks/auth';
import driverKilometersService, {
  IDriverKilometerCreate,
  IDriverKilometerUpdate,
  IDriverKilometersListResponse,
} from '../../../../services/driverKilometersService';

interface IDriver {
  id: string;
  name: string;
}

export function DriverKilometerTable() {
  const { user } = useAuth();
  const isWideVersion = useBreakpointValue({
    base: false,
    lg: true,
  });

  const [dataTable, setDataTable] = useState<
    IDriverKilometersListResponse | undefined
  >();
  const [drivers, setDrivers] = useState<IDriver[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadingId, setLoadingId] = useState('');
  const [loadingTable, setLoadingTable] = useState(false);
  const [open, setOpen] = useState('');
  const [updateTable, setUpdateTable] = useState<string>('');
  const [page, setPage] = useState(1);

  // Carrega lista de condutores
  useEffect(() => {
    // TODO: Descomentar quando a API estiver pronta
    driverKilometersService
      .listDrivers()
      .then(response => {
        setDrivers(response);
      })
      .catch(err => {
        console.error(err);
        apllyToast('error', 'Erro ao carregar lista de condutores');
      });
  }, []);

  // Carrega quilometragens
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingTable(true);

        // TODO: Descomentar quando a API estiver pronta
        const response = await driverKilometersService.list({
          conductor_id: user.flag_7 ? selectedDriver : user.id,
          page,
          take: 6,
        });
        setDataTable(response);

        setLoadingTable(false);
      } catch (err) {
        console.error(err);
        apllyToast('error', 'Problemas ao carregar quilometragens');
        setLoadingTable(false);
      }
    };

    loadData();
  }, [selectedDriver, page, updateTable, user]);

  const handleAddNewDriverKilometer = useCallback(
    async (data: IDriverKilometerCreate) => {
      try {
        const dataWithConductor = {
          ...data,
          conductor_id: user.id,
        };

        await driverKilometersService.create(dataWithConductor);

        console.log('Mock: Adicionando quilometragem:', dataWithConductor);
        setUpdateTable(Date.now().toString());
        apllyToast('success', 'Quilometragem adicionada com sucesso (MOCK)');
      } catch (err) {
        console.error(err);
        apllyToast('warning', 'Problemas ao adicionar quilometragem');
      }
    },
    [user.id],
  );

  const handleEditDriverKilometer = useCallback(
    async (id: string, data: IDriverKilometerUpdate) => {
      try {
        // TODO: Descomentar quando a API estiver pronta
        // await driverKilometersService.update(id, data);

        console.log('Mock: Editando quilometragem:', id, data);
        setUpdateTable(Date.now().toString());
        apllyToast('success', 'Quilometragem atualizada com sucesso (MOCK)');
      } catch (err) {
        console.error(err);
        apllyToast('warning', 'Problemas ao atualizar quilometragem');
      }
    },
    [],
  );

  const handleVerifyDateIsNew = useCallback((date: string | number | Date) => {
    if (differenceInSeconds(new Date(), new Date(date)) < 600) {
      return true;
    }
    return false;
  }, []);

  const handleReceiptChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      try {
        setLoading(true);
        setLoadingId(e.target.id);
        if (e.target.files && e.target.files[0]) {
          // TODO: Descomentar quando a API estiver pronta
          // await driverKilometersService.uploadFile(
          //   e.target.id,
          //   e.target.files[0],
          // );

          console.log(
            'Mock: Upload de comprovante:',
            e.target.id,
            e.target.files[0].name,
          );
          apllyToast('success', 'Comprovante adicionado (MOCK)');
          setUpdateTable(Date.now().toString());
          setLoading(false);
          setLoadingId('');
        }
      } catch (err) {
        console.error(err);
        apllyToast('warning', 'Problemas ao adicionar comprovante');
        setLoading(false);
        setLoadingId('');
      }
    },
    [],
  );

  const handleOpenImage = useCallback((id: string) => {
    setOpen(id);
  }, []);

  const handleCloseImage = useCallback(() => {
    setOpen('');
  }, []);

  const handleUpdateTable = useCallback(() => {
    setUpdateTable(Date.now().toString());
  }, []);

  const handleDriverChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      setSelectedDriver(e.target.value);
      setPage(1);
    },
    [],
  );

  return (
    <Box>
      <Box flex="1" borderRadius={8} bg="gray.800" p={['4', '8']}>
        <Flex m="8" justify="space-between" align="center">
          <Flex direction="column">
            <Flex align="center">
              <Heading size="md" fontWeight="normal">
                Quilometragem de Condutores
              </Heading>
              <Text color="gray.400" fontSize="xs" ml="2" mt="1">
                (total de registros: {dataTable?.pagination.total || 0})
              </Text>
            </Flex>

            {user.flag_7 && (
              <Box mt="4" maxW="300px">
                <Text fontSize="sm" mb="2">
                  Filtrar por condutor:
                </Text>
                <ChakraSelect
                  placeholder="Todos os condutores"
                  value={selectedDriver}
                  onChange={handleDriverChange}
                  bg="gray.700"
                  borderColor="gray.600"
                  size="sm"
                >
                  {drivers.map(driver => (
                    <option key={driver.id} value={driver.id}>
                      {driver.name}
                    </option>
                  ))}
                </ChakraSelect>
              </Box>
            )}
          </Flex>

          <Box>
            <ModalAddNewDriverKilometer
              handleAddNewDriverKilometer={handleAddNewDriverKilometer}
            />
          </Box>
        </Flex>

        {loadingTable ? (
          <ScaleFade initialScale={0.9} in>
            <Center>
              <Spinner size="sm" />
            </Center>
          </ScaleFade>
        ) : (
          <>
            {dataTable && (dataTable.driverKilometers?.length ?? 0) <= 0 ? (
              <ScaleFade initialScale={0.9} in>
                <Center>
                  <Flex flexDirection="row">
                    <Icon as={RiDraftLine} fontSize="20" mr="2" />
                    <Text>Sem registro de quilometragens</Text>
                  </Flex>
                </Center>
              </ScaleFade>
            ) : (
              <>
                <ScaleFade initialScale={0.9} in>
                  <Table colorScheme="whiteAlpha">
                    <Thead>
                      <Tr>
                        <Th px={['2', '4', '6']} color="gray.300" width="8">
                          <Checkbox colorScheme="pink" />
                        </Th>

                        <Th>Data</Th>
                        <Th>Condutor</Th>
                        {isWideVersion && <Th>KM Inicial</Th>}
                        {isWideVersion && <Th>KM Final</Th>}
                        <Th>KM Percorrido</Th>
                        {isWideVersion && <Th>Destino</Th>}
                        {isWideVersion && <Th>Motivo</Th>}
                        {isWideVersion && <Th>Observações</Th>}
                        {isWideVersion && <Th>Anexo</Th>}

                        {isWideVersion && <Th width="8">Editar</Th>}
                      </Tr>
                    </Thead>

                    <Tbody>
                      {dataTable?.driverKilometers.map(data => (
                        <Tr key={data.id}>
                          <Td px={['2', '4', '6']}>
                            <Box mb="2">
                              {handleVerifyDateIsNew(data.created_at) ? (
                                <Badge colorScheme="green">Novo</Badge>
                              ) : (
                                ''
                              )}
                            </Box>
                            <Checkbox colorScheme="pink" />
                          </Td>

                          <Td>
                            <Box>
                              <Text fontWeight="medium">
                                {format(new Date(data.date), "dd 'de' MMMM", {
                                  locale: ptBR,
                                })}
                              </Text>
                              <Text fontSize="sm" color="gray.300">
                                {format(new Date(data.date), 'HH:mm', {
                                  locale: ptBR,
                                })}
                              </Text>
                            </Box>
                          </Td>

                          <Td>
                            <Box>
                              <Text fontWeight="medium">
                                {data?.conductor.name}
                              </Text>
                            </Box>
                          </Td>

                          {isWideVersion && <Td>{data.km_start} km</Td>}
                          {isWideVersion && <Td>{data.km_end} km</Td>}
                          <Td>
                            <Text fontWeight="bold">{data.km_traveled} km</Text>
                          </Td>
                          {isWideVersion && <Td>{data.destination}</Td>}
                          {isWideVersion && <Td>{data.reason}</Td>}
                          {isWideVersion && <Td>{data.observation || '-'}</Td>}

                          {isWideVersion && (
                            <Td>
                              {data.file ? (
                                <Tooltip
                                  hasArrow
                                  label="Visualizar comprovante"
                                >
                                  <Box>
                                    <Button
                                      onClick={() => handleOpenImage(data.id)}
                                      size="sm"
                                      fontSize="sm"
                                      bg="green.500"
                                    >
                                      <Icon
                                        as={RiAttachmentLine}
                                        fontSize="20"
                                      />
                                      <ModalVisualizeImage
                                        url={data.file_url}
                                        open={open === data.id}
                                        handleCloseImage={handleCloseImage}
                                        idSupply={data.id}
                                        hadleUpdateTable={handleUpdateTable}
                                      />
                                    </Button>
                                  </Box>
                                </Tooltip>
                              ) : (
                                <Tooltip hasArrow label="Adicionar comprovante">
                                  <Box
                                    as="label"
                                    px="3"
                                    py="1.5"
                                    borderRadius="6"
                                    htmlFor={data.id}
                                    bg="tomato"
                                    cursor="pointer"
                                  >
                                    {loading && loadingId === data.id ? (
                                      <Spinner size="sm" />
                                    ) : (
                                      <Icon
                                        as={RiAttachmentLine}
                                        fontSize="20"
                                      />
                                    )}
                                    <Box
                                      display="none"
                                      as="input"
                                      type="file"
                                      id={data.id}
                                      onChange={handleReceiptChange}
                                    />
                                  </Box>
                                </Tooltip>
                              )}
                            </Td>
                          )}

                          {isWideVersion && (
                            <Td>
                              <ModalEditDriverKilometer
                                id_driver_kilometer={data.id}
                                handleEditDriverKilometer={
                                  handleEditDriverKilometer
                                }
                              />
                            </Td>
                          )}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </ScaleFade>
                <Pagination
                  totalCountOfRegisters={dataTable?.pagination.total || 0}
                  currentPage={page}
                  onPageChange={setPage}
                  registersPerPage={dataTable?.pagination.take || 0}
                />
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
